import { useEffect, useRef, useState } from 'react';
import { depositFormService } from '../api/services';
import { deleteLocalDraft, getLocalDraft, newDraftId, saveLocalDraft } from '../utils/depositDrafts';

const fileKinds = ['id_front', 'id_back', 'payment_proof'];
const hasContent = (form, files, initial) => Object.entries(form).some(([key, value]) => value !== initial[key] && value !== '' && value != null) || Object.values(files).some(Boolean);

export default function useDepositDraft({ userId, form, files, restore }) {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const requestedId = params.get('draft');
  const activeKey = `deposit-active-draft:${userId}${params.has('new') ? `:new:${params.get('new')}` : ''}`;
  const existing = useRef(Boolean(params.get('draft') || sessionStorage.getItem(activeKey)));
  const [id] = useState(() => {
    if (!userId) return null;
    return params.get('draft') || sessionStorage.getItem(activeKey) || newDraftId();
  });
  const [ready, setReady] = useState(!userId);
  const [status, setStatus] = useState('');
  const [completed, setCompleted] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const restoreRef = useRef(restore); restoreRef.current = restore;
  const latest = useRef(null), running = useRef(null), queued = useRef(null), timer = useRef(null);
  const uploaded = useRef({});
  const stopped = useRef(false), mounted = useRef(true);
  const baseline = useRef(form), hasDraft = useRef(false);
  const report = (text) => { if (mounted.current) setStatus(text); };

  useEffect(() => {
    mounted.current = true;
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      let local;
      try { local = await getLocalDraft(id, userId); } catch { /* Remote drafts still work when browser storage is unavailable. */ }
      if (cancelled) return;
      if (!existing.current && !local) {
        sessionStorage.setItem(activeKey, id); setReady(true); return;
      }
      let remote;
      try { remote = await depositFormService.getDraft(id); }
      catch (error) {
        if (error.response?.status === 409) {
          await deleteLocalDraft(id).catch(() => {});
          if (!cancelled) {
            sessionStorage.removeItem(activeKey);
            setLoadError('Bản nháp này đã hoàn thành hoặc đã được xóa. Bạn có thể tạo phiếu mới.');
          }
          return;
        }
        if (!local && requestedId && error.response?.status === 404) {
          if (!cancelled) setLoadError('Không tìm thấy bản nháp của tài khoản này. Bạn có thể tạo phiếu mới.');
          return;
        }
        if (!local && error.response?.status !== 404) {
          if (!cancelled) setLoadError('Không tải được bản nháp. Hãy tải lại trang để thử lại; dữ liệu cũ chưa bị thay đổi.');
          return;
        }
      }
      if (cancelled) return;
      try {
        setEditingId(remote?.deposit_form_id || local?.depositId || null);
        const restoredRoomId = remote?.room_id || local?.roomId || null;
        setRoomId(restoredRoomId);
        if (local && (!remote || new Date(local.updatedAt) >= new Date(remote.updated_at))) {
          hasDraft.current = true;
          restoreRef.current(local.form, local.files, { roomId: restoredRoomId });
          report('Đã khôi phục bản nháp trên thiết bị.');
        } else if (remote) {
          hasDraft.current = true;
          const restoredFiles = {};
          await Promise.all(fileKinds.map(async (kind) => {
            if (!remote.attachments[kind]) { restoredFiles[kind] = null; return; }
            const blob = await depositFormService.getDraftFile(id, kind);
            const extension = blob.type.split('/')[1] || 'png';
            restoredFiles[kind] = new File([blob], `${kind}.${extension}`, { type: blob.type });
          }));
          if (cancelled) return;
          uploaded.current = { ...restoredFiles };
          restoreRef.current(remote.form_data, restoredFiles, { roomId: restoredRoomId });
          report('Đã khôi phục bản nháp. Bạn có thể tiếp tục nhập.');
        }
        sessionStorage.setItem(activeKey, id);
        setReady(true);
      } catch {
        setLoadError('Không tải đủ ảnh bản nháp. Hãy tải lại trang để thử lại; dữ liệu cũ vẫn được giữ.');
      }
    })();
    return () => { cancelled = true; mounted.current = false; clearTimeout(timer.current); };
  }, [id, userId, activeKey, requestedId]);

  const sync = (snapshot) => {
    queued.current = snapshot;
    if (running.current) return running.current;
    running.current = (async () => {
      while (queued.current) {
        const next = queued.current; queued.current = null;
        const data = new FormData();
        data.append('form_data', JSON.stringify(next.form));
        data.append('files_state', JSON.stringify(Object.fromEntries(fileKinds.map((kind) => [kind, Boolean(next.files[kind])]))));
        fileKinds.forEach((kind) => { if (next.files[kind] && uploaded.current[kind] !== next.files[kind]) data.append(kind, next.files[kind]); });
        await depositFormService.saveDraft(id, data);
        uploaded.current = { ...next.files };
        report('Đã tự lưu bản nháp.');
      }
    })().finally(() => { running.current = null; });
    return running.current;
  };
  const syncRef = useRef(sync); syncRef.current = sync;

  useEffect(() => {
    if (!ready || !id || stopped.current || (!hasDraft.current && !hasContent(form, files, baseline.current))) return;
    hasDraft.current = true;
    const snapshot = { id, userId, depositId: editingId, roomId, form: { ...form }, files: { ...files }, updatedAt: new Date().toISOString() };
    latest.current = snapshot;
    report('Đang lưu bản nháp…');
    let localSaved = false;
    saveLocalDraft(snapshot).then(() => { localSaved = true; }).catch(() => report('Chưa lưu được trên thiết bị; đang thử lưu lên hệ thống…'));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      syncRef.current(snapshot).catch(() => report(localSaved ? 'Đã lưu trên thiết bị. Chưa đồng bộ được lên hệ thống; sẽ thử lại khi bạn nhập tiếp.' : 'Chưa tự lưu được. Hãy giữ trang này và bấm Thử lưu lại.'));
    }, 800);
    return () => clearTimeout(timer.current);
  }, [ready, id, userId, form, files, editingId, roomId]);

  return {
    id, editingId, ready, status, completed, loadError,
    retry: () => latest.current && syncRef.current(latest.current).catch(() => report('Chưa đồng bộ được bản nháp. Vui lòng thử lại.')),
    beginFinalize: async () => {
      if (!id) return null;
      stopped.current = true; clearTimeout(timer.current);
      if (running.current) await running.current;
      const snapshot = { id, userId, depositId: editingId, roomId, form, files, updatedAt: new Date().toISOString() };
      await saveLocalDraft(snapshot).catch(() => {});
      await syncRef.current(snapshot);
      return id;
    },
    resume: () => { stopped.current = false; },
    complete: async () => {
      stopped.current = true; queued.current = null; clearTimeout(timer.current);
      setCompleted(true); report('Phiếu cọc đã hoàn thành.');
      if (id) await deleteLocalDraft(id).catch(() => {});
      Object.keys(sessionStorage).filter((key) => key.startsWith(`deposit-active-draft:${userId}`) && sessionStorage.getItem(key) === id).forEach((key) => sessionStorage.removeItem(key));
    },
  };
}
